import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Barcode, Calendar, Lightbulb, Lock, TriangleAlert, type LucideIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { dangerousGoodIcon } from '@/shared/lib/dangerous-goods';
import { expiryClass, formatDisplayDate } from '@/shared/lib/date';
import { TemperatureRangeBadge } from '@/shared/ui/temperature-range-badge';

export interface LineDangerousGood {
  /** e.g. `UN-1230` */
  unId: string;
  description: string;
  classDescription?: string | null;
  /** UN hazard class or division, e.g. `3` or `2.1`; picks the icon */
  classOrDivision?: string | null;
}

/** What a consignment line carries for the markers after its description, as legacy `requestLineDesc.xhtml` */
export interface LineMarkersData {
  isDangerous?: boolean;
  dangerousGood?: LineDangerousGood | null;
  isColdChain?: boolean;
  coldMinTemp?: number | null;
  coldMaxTemp?: number | null;
  isFragile?: boolean;
  isRegulated?: boolean;
  batchNumber?: string | null;
  /** `YYYY-MM-DD` */
  expiryDate?: string | null;
}

/** The project settings `isBatchNumberActive` / `isExpiryDateActive` */
export interface LineMarkerFlags {
  showBatch?: boolean;
  showExpiry?: boolean;
}

interface LineMarkersProps extends LineMarkerFlags {
  line: LineMarkersData;
  /** The line description the markers follow */
  children: ReactNode;
}

const EXPIRY_BADGE = {
  expired: { variant: 'destructive' },
  aboutExpire: { variant: 'warning' },
  soonExpire: { variant: 'warning', className: 'bg-warning-100' },
  notExpired: { variant: 'outline' }
} as const;

const MarkerIcon = ({ icon: Icon, title }: { icon: LucideIcon; title: string }) => (
  <span role="img" aria-label={title} title={title} className="inline-flex">
    <Icon className="size-4" aria-hidden />
  </span>
);

const dangerousGoodTitle = ({ unId, description, classDescription }: LineDangerousGood) =>
  `${unId} – ${description}` + (classDescription ? ` (${classDescription})` : '');

/** A line description followed by its markers in legacy order: dangerous, cold chain, fragile, regulated, batch, expiry. */
export const LineMarkers = ({ line, showBatch = true, showExpiry = true, children }: LineMarkersProps) => {
  const { t } = useTranslation();
  const { isDangerous, dangerousGood, isColdChain, isFragile, isRegulated } = line;
  const batchNumber = showBatch ? line.batchNumber?.trim() : undefined;
  const expiryDate = showExpiry ? line.expiryDate : undefined;

  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {children}
      {isDangerous && (
        <MarkerIcon
          icon={dangerousGood ? dangerousGoodIcon(dangerousGood.classOrDivision) : TriangleAlert}
          title={dangerousGood ? dangerousGoodTitle(dangerousGood) : t('requestline.isDangerous.label')}
        />
      )}
      {isColdChain && <TemperatureRangeBadge min={line.coldMinTemp} max={line.coldMaxTemp} />}
      {isFragile && <MarkerIcon icon={Lightbulb} title={t('requestline.isFragile.label')} />}
      {isRegulated && <MarkerIcon icon={Lock} title={t('requestline.isRegulated.label')} />}
      {batchNumber && (
        <Badge variant="outline" title={t('requestline.batchNumber.label')}>
          <Barcode aria-hidden />
          {batchNumber}
        </Badge>
      )}
      {expiryDate && (
        <Badge {...EXPIRY_BADGE[expiryClass(expiryDate)]} title={t('requestline.expiryDate.label')}>
          <Calendar aria-hidden />
          {formatDisplayDate(expiryDate)}
        </Badge>
      )}
    </span>
  );
};
